import {
  IocSelectorOption,
  IocSelectorService,
} from './../../../../_shared/components/ioc-selector/ioc-selector.service';
import { Injectable, inject } from '@angular/core';
import { Match, Side } from '../../../../_shared/models/match.model';
import {
  ScoreFormat,
  SetType,
} from '../../../../_shared/models/score-format.model';
import { BoardHelpService } from '../../board-help.service';
import {
  Point,
  Server,
  PlayerInfo,
  PlayerCourtPosition,
} from '../../../../_shared/models/point.model';
import { CourtSide } from '../../../../_shared/models/enums/court-side';

@Injectable({
  providedIn: 'root',
})
export class TableTennisTrackerBrainService {
  private iocSelectorService = inject(IocSelectorService);
  private bhs = inject(BoardHelpService);

  private side1PlayerPosition: PlayerCourtPosition = {};
  private side2PlayerPosition: PlayerCourtPosition = {};

  constructor() {}

  async selectServer(
    match: Match,
    showCancelButton: boolean,
    serverNumber?: number,
    sideNumber?: number | undefined
  ): Promise<Server | undefined> {
    const serverTitle: string = 'Select server';
    const players = this.getPlayers(match);
    const serverOptions = this.getServerOptions(match, players, serverNumber, sideNumber);

    let selectedServer: Server = await this.iocSelectorService.openPopover(
      serverTitle,
      serverOptions,
      showCancelButton
    );

    if (!selectedServer) return undefined;

    const returnerPlayerNumber = match.matchType == 'D' ? await this.selectReturner(
      match,
      selectedServer?.returnerSideNumber,
      showCancelButton
    ): 1;

    if (!returnerPlayerNumber) return undefined;

    const returnerSideNumber = selectedServer?.returnerSideNumber ?? (selectedServer?.sideNumber === 1 ? 2 : 1);
    selectedServer.returnerPlayerNumber = returnerPlayerNumber;
    selectedServer.returnerPlayerId =
      match?.sides?.[returnerSideNumber - 1]?.players?.[returnerPlayerNumber - 1]?.participant?._id ?? null;
    // In table tennis doubles, server and returner are always on DEUCE side
    selectedServer.returningSide = CourtSide.DEUCE;

    this.assignPlayerPositions(players, selectedServer);
    return selectedServer;
  }

  /**
   * Creates a server object with the specified side and player number
   */
  async createServerObject(
    match: Match,
    sideNumber: number,
    playerNumber: number,
    selectReturner: boolean = false,
    previousServer?: Server | null
  ): Promise<Server | undefined> {
    const players = this.getPlayers(match);
    const returnerSideNumber = sideNumber === 1 ? 2 : 1;
    
    const server: Server = {
      sideNumber,
      playerNumber,
      player: players[sideNumber]?.[playerNumber - 1]?.player || null,
      returnerSideNumber,
      returningSide: CourtSide.DEUCE,
      returnerPlayerNumber: 1,
      returnerPlayerId: match?.sides?.[returnerSideNumber - 1]?.players?.[0]?.participant?._id ?? null,
    };
    
    // For doubles, automatically determine returner based on rotation pattern
    if (match.matchType === 'D' && selectReturner) {
      const returnerPlayerNumber = this.getReturnerPlayerNumber(match, sideNumber, playerNumber, previousServer);
      server.returnerPlayerNumber = returnerPlayerNumber;
      server.returnerPlayerId =
        match?.sides?.[returnerSideNumber - 1]?.players?.[returnerPlayerNumber - 1]?.participant?._id ?? null;
      // In table tennis doubles, server and returner are always on DEUCE side
      server.returningSide = CourtSide.DEUCE;
      server.player = match?.sides?.[sideNumber - 1]?.players?.[playerNumber - 1]?.participant?._id ?? "";
    }

    // Assign player positions, passing previousServer to handle side switching
    if (match.matchType === 'D') {
      this.assignPlayerPositions(players, server, previousServer);
    }

    return server;
  }

  private getPlayers(match: Match): PlayerInfo {
    const players: any = {};

    match.sides.forEach((side, i) => {
      players[i + 1] = side.players.map((player, j) => ({
        playerName: this.bhs.getPlayersNames(
          match,
          side.sideNumber,
          'full',
          j + 1
        ),
        sideNumber: i + 1,
        playerNumber: j + 1,
        player: player.participant?._id || null,
      }));
    });

    return players;
  }

  private getServerOptions(match: Match, players: any, serverNumber?: number, sideNumber?: number): IocSelectorOption[] {
    return (Object.values(players) as any[][]).flatMap((sidePlayers: any[], i) =>
      sidePlayers.filter(player=> {
        return !sideNumber || player.sideNumber === sideNumber
      }).map((player) => ({
        label: player.playerName,
        value: {
          ...player,
          serverNumber,
          returnerSideNumber: i === 0 ? 2 : 1,
          returningSide: CourtSide.DEUCE,
        },
      }))
    );
  }

  /**
   * Automatically determines the returner player number based on rotation pattern
   * Table Tennis Doubles Rotation Pattern follows a 4-position cycle:
   * The returner is determined by the next player in the rotation cycle after the current server.
   * 
   * Rotation scenarios (Team A: A1, A2 | Team B: B1, B2):
   * 1) A1 → B1, B1 → A2, A2 → B2, B2 → A1 (repeat)
   * 2) A1 → B2, B2 → A2, A2 → B1, B1 → A1 (repeat)
   * 3) A2 → B1, B1 → A1, A1 → B2, B2 → A2 (repeat)
   * 4) A2 → B2, B2 → A1, A1 → B1, B1 → A2 (repeat)
   * 5) B1 → A1, A1 → B2, B2 → A2, A2 → B1 (repeat)
   * 6) B1 → A2, A2 → B2, B2 → A1, A1 → B1 (repeat)
   * 7) B2 → A1, A1 → B1, B1 → A2, A2 → B2 (repeat)
   * 8) B2 → A2, A2 → B1, B1 → A1, A1 → B2 (repeat)
   * 
   * The pattern: returner is the next player in the rotation cycle after the current server.
   */
  private getReturnerPlayerNumber(
    match: Match,
    serverSideNumber: number,
    serverPlayerNumber: number,
    previousServer?: Server | null
  ): number {
    // Get the first server of the current game to determine the rotation cycle
    const lastGame = this.bhs.getLastGame(match);
    const firstServer = lastGame?.points?.[0]?.server;
    
    // If no first server in current game, try to use previous server as reference
    // but we need both server and returner info to build the cycle
    if (!firstServer || !firstServer.returnerPlayerNumber) {
      // Fallback to diagonal pattern if no first server with returner info available
      if (serverSideNumber === 1) {
        return serverPlayerNumber;
      } else {
        return serverPlayerNumber === 1 ? 2 : 1;
      }
    }

    // Determine the rotation cycle based on the first server and returner
    const firstServerSide = firstServer.sideNumber ?? 1;
    const firstServerPlayer = firstServer.playerNumber ?? 1;
    const firstReturnerSide = firstServer.returnerSideNumber ?? (firstServerSide === 1 ? 2 : 1);
    const firstReturnerPlayer = firstServer.returnerPlayerNumber ?? 1;

    // Build the 4-position cycle:
    // Position 1: firstServer
    // Position 2: firstReturner (who becomes the next server)
    // Position 3: other player on firstServer's side (who becomes the returner for position 2)
    // Position 4: other player on firstReturner's side (who becomes the returner for position 3)
    const cycle: Array<{ s: number; p: number }> = [
      { s: firstServerSide, p: firstServerPlayer },           // Position 1
      { s: firstReturnerSide, p: firstReturnerPlayer },      // Position 2
    ];

    // Position 3: other player on firstServer's side
    const otherPlayerOnFirstServerSide = firstServerPlayer === 1 ? 2 : 1;
    cycle.push({ s: firstServerSide, p: otherPlayerOnFirstServerSide });

    // Position 4: other player on firstReturner's side
    const otherPlayerOnFirstReturnerSide = firstReturnerPlayer === 1 ? 2 : 1;
    cycle.push({ s: firstReturnerSide, p: otherPlayerOnFirstReturnerSide });

    // Find the current server's position in the cycle
    const currentIndex = cycle.findIndex(
      pos => pos.s === serverSideNumber && pos.p === serverPlayerNumber
    );

    if (currentIndex >= 0) {
      // The returner is the next player in the cycle
      const returnerIndex = (currentIndex + 1) % 4;
      const returnerPos = cycle[returnerIndex];
      
      // Verify that the returner is on the opposite side of the server
      const expectedReturnerSide = serverSideNumber === 1 ? 2 : 1;
      if (returnerPos.s === expectedReturnerSide) {
        return returnerPos.p;
      }
      // If side doesn't match, the cycle might be incorrect - this shouldn't happen
      // but we'll fall through to the fallback
    }

    // Fallback: if current server not found in cycle or side mismatch, use diagonal pattern
    // This ensures we always return a valid returner player number
    if (serverSideNumber === 1) {
      return serverPlayerNumber;
    } else {
      return serverPlayerNumber === 1 ? 2 : 1;
    }
  }

  private async selectReturner(
    match: Match,
    returnerSideNumber: number = 1,
    showCancelButton: boolean
  ): Promise<number> {
    const returnerTitle = 'Select returner';
    const returnerOptions = match.sides[returnerSideNumber - 1].players.map(
      (_, i) => ({
        label:
          this.bhs.getPlayersNames(match, returnerSideNumber, 'full', i + 1) ??
          '',
        value: i + 1,
      })
    );

    return this.iocSelectorService.openPopover(
      returnerTitle,
      returnerOptions,
      showCancelButton
    );
  }

  private assignPlayerPositions(players: any, selectedServer: Server, previousServer?: Server | null): void {
    // Check if a team is switching from serving to receiving
    // If previousServer exists and the serving side changed, the previous serving team becomes receiving
    if (previousServer && previousServer.sideNumber && previousServer.sideNumber !== selectedServer.sideNumber) {
      // The previous serving team is now receiving - swap their positions
      const previousServingSide = previousServer.sideNumber;
      this.swapTeamPositions(previousServingSide);
    }
    
    [1, 2].forEach((side) => {
      players[side].forEach((player: PlayerInfo) => {
        const isServerOrReturner =
          selectedServer.sideNumber === side
            ? player.playerNumber === selectedServer.playerNumber
            : player.playerNumber === selectedServer.returnerPlayerNumber;

        this[`side${side}PlayerPosition`][isServerOrReturner ? 'deuce' : 'ad'] =
          player;
      });
    });
  }

  /**
   * Swaps the deuce and ad positions for a team
   * Used when a team switches from serving to receiving
   */
  private swapTeamPositions(side: number): void {
    const position = this[`side${side}PlayerPosition`];
    if (position && position.deuce && position.ad) {
      const temp = position.deuce;
      position.deuce = position.ad;
      position.ad = temp;
    }
  }

  public getPlayerPosition(side: number): PlayerCourtPosition | null {
    return this[`side${side}PlayerPosition`] ?? null;
  }

  /**
   * Restores player positions from a point's playerPositions data
   * Used when starting a new point to restore positions from the previous point
   */
  public restorePlayerPositions(playerPositions: { side1?: PlayerCourtPosition; side2?: PlayerCourtPosition } | undefined): void {
    if (!playerPositions) return;
    
    if (playerPositions.side1) {
      this.side1PlayerPosition = { ...playerPositions.side1 };
    }
    if (playerPositions.side2) {
      this.side2PlayerPosition = { ...playerPositions.side2 };
    }
  }

  /**
   * Table Tennis server change logic
   * - Every 2 points, the server changes
   * - At deuce (10-10), server changes every 1 point
   */
  shouldChangeServer(
    match: Match,
    currentPoint: Point,
    pointWinnerSideNumber: number
  ): boolean {
    const scoreFormat = this.bhs.getScoreFormat(match.matchFormat, match.discipline);
    const gamePointsToWin = scoreFormat.numberOfPointsInGame || 11;
    const side1Score = currentPoint.side1Score ?? 0;
    const side2Score = currentPoint.side2Score ?? 0;
    const totalPoints = side1Score + side2Score;

    // Check if we're at deuce (both sides have reached at least gamePointsToWin - 1)
    const isDeuce = (side1Score >= gamePointsToWin - 1 && side2Score >= gamePointsToWin - 1);

    // At deuce, server changes every 1 point
    if (isDeuce) {
      return true;
    }

    // Normal play: change server every 2 points.
    // Do NOT change server at totalPoints === 0 (start of game).
    return totalPoints > 0 && totalPoints % 2 === 0;
  }

  getNextServer(match: Match, currentPoint: Point, previousServer: Server | null): { sideNumber: number; playerNumber: number } | null {
    if (!previousServer) return null;

    const totalPoints = (currentPoint.side1Score ?? 0) + (currentPoint.side2Score ?? 0);
    const scoreFormat = this.bhs.getScoreFormat(match.matchFormat, match.discipline);
    const gamePointsToWin = scoreFormat.numberOfPointsInGame || 11;

    const lastGame = this.bhs.getLastGame(match);
    const firstServer = lastGame?.points?.[0]?.server ?? previousServer;
    const firstServerSide = firstServer.sideNumber ?? 1;
    const firstServerPlayer = firstServer.playerNumber ?? 1;
    const firstReturnerSide = firstServer.returnerSideNumber ?? (firstServerSide === 1 ? 2 : 1);
    const firstReturnerPlayer = firstServer.returnerPlayerNumber ?? 1;

    const isDeuce =
      (currentPoint.side1Score ?? 0) >= gamePointsToWin - 1 &&
      (currentPoint.side2Score ?? 0) >= gamePointsToWin - 1;

    let servingSide: number;
    let playerNumber: number;

    if (match.matchType === 'D') {
      // Doubles rotation (4-position cycle) - build cycle based on first server and returner
      const cycle: Array<{ s: number; p: number }> = [
        { s: firstServerSide, p: firstServerPlayer },
        { s: firstReturnerSide, p: firstReturnerPlayer },
      ];
      
      // Position 3: other player on firstServer's side
      const otherPlayerOnFirstServerSide = firstServerPlayer === 1 ? 2 : 1;
      cycle.push({ s: firstServerSide, p: otherPlayerOnFirstServerSide });
      
      // Position 4: other player on firstReturner's side
      const otherPlayerOnFirstReturnerSide = firstReturnerPlayer === 1 ? 2 : 1;
      cycle.push({ s: firstReturnerSide, p: otherPlayerOnFirstReturnerSide });

      if (isDeuce) {
        // alternate every point: move to next position from previousServer in the cycle
        const prevIndex = cycle.findIndex(
          pos => pos.s === previousServer.sideNumber && pos.p === (previousServer.playerNumber ?? 1)
        );
        const nextIndex = (prevIndex < 0 ? 0 : (prevIndex + 1) % 4);
        servingSide = cycle[nextIndex].s;
        playerNumber = cycle[nextIndex].p;
      } else {
        // normal play: determine 2-point blocks and map to rotation
        const blockIndex = Math.floor(totalPoints / 2);
        const rotationPos = blockIndex % 4;
        servingSide = cycle[rotationPos].s;
        playerNumber = cycle[rotationPos].p;
      }
    } else {
      // Singles
      const otherSide = firstServerSide === 1 ? 2 : 1;
      if (isDeuce) {
        // parity-based at deuce
        servingSide = totalPoints % 2 === 0 ? firstServerSide : otherSide;
      } else {
        // normal 2-point blocks
        const blockIndex = Math.floor(totalPoints / 2);
        servingSide = blockIndex % 2 === 0 ? firstServerSide : otherSide;
      }
      playerNumber = 1;
    }

    return { sideNumber: servingSide, playerNumber };
  }

  /**
   * Check if a game is won in table tennis
   * Table Tennis Rules:
   * - Games are won by reaching 11 points with a lead of at least 2 points
   * - If both players reach 10-10 (deuce), continue until one player leads by 2 points
   */
  isAddedGame(
    match: Match,
    scoreFormat: ScoreFormat,
    nextToLastPointNumberOfPointsWonForWinner: number,
    nextToLastPointNumberOfPointsWonForLoser: number,
    winningSide: number
  ): boolean {
    const numberOfPointsInGame = scoreFormat.numberOfPointsInGame || 11;
    const currentWinnerScore = nextToLastPointNumberOfPointsWonForWinner + 1;
    const currentLoserScore = nextToLastPointNumberOfPointsWonForLoser;

    // Table tennis scoring rules:
    // - First to reach 11 points with a 2-point lead wins
    // - If both reach 10 points (deuce), continue until someone wins by 2
    
    // Game is won when:
    // 1. Reached minimum points (11 by default) AND won by at least 2 points
    // This covers both normal wins (11-9, 11-8, etc.) and deuce wins (12-10, 13-11, etc.)
    if (currentWinnerScore >= numberOfPointsInGame && currentWinnerScore >= currentLoserScore + 2) {
      return true;
    }

    return false;
  }

  /**
   * Checks if the current set is the final set
   */
  isLastSet(match: Match, scoreFormat: ScoreFormat): boolean {
    return match.sets.length === scoreFormat.numberOfSets;
  }

  /**
   * Swaps sides when a set ends
   * In table tennis, players switch sides after each set
   */
  swapSides(match: Match): void {
    let isReverse = !this.bhs.getLastPoint(match)!.extensions!.find((ext) => ext.name === 'areSidesReversed')!.value;
    if (this.bhs.getLastPoint(match)?.extensions?.find((ext) => ext.name === 'areSidesReversed')) {        
      this.bhs.getLastPoint(match)!.extensions!.find((ext) => ext.name === 'areSidesReversed')!.value = isReverse;
    }
  }
}

